import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useSalonBrand } from '../../brand/useSalonBrand'
import { marketFor, normHex } from '../../email-composer/email/theme'
import { loadItems } from '../../email-composer/components/ContentPanel'
import { hapticTick, hapticSuccess } from '../../../../../_utils/haptics'
import { POST_TYPES, TYPE_ORDER, makePost, realPost, designName, upgradePost, showcaseFor } from '../data/postTypes'
import { loadPosts, subscribePosts, keptHereOnly, deletePost, restorePost, savePost, isDue, loadFavourites, subscribeFavourites, addFavourite, findFavourite, removeFavourite, restoreFavourite } from '../data/store'
import { favouriteFrom, postFromFavourite } from '../data/favourites'
import { buildIdeas } from '../data/ideas'
import { findFreeSlots } from '../data/freeSlots'
import { loadScheduledCampaigns, todayItems } from '../data/schedule'
import { reviewPost, shareable, isShared, reviewWhen } from '../data/reviews'
import { townOf, buildCaption } from '../data/captions'
import { parseRequest, createFromRequest, missingFacts, NOT_UNDERSTOOD_HOME } from '../data/ask'
import { baileyReady, briefFor, reviewsFor, askBailey as askBaileyAI, applyEdit, summaryOf, isNews } from '../data/askAI'
import { useGatherAsk, withStylist } from '../data/askData'
import { familyForLook } from '../render/families'
import { postTheme } from '../render/render'
import MarketingCalendar from '../../calendar/components/MarketingCalendar'
import PostStudio from './PostStudio'
import Preview from './Preview'
import LazyPreview from './studio/LazyPreview'
import DesignSheet from './DesignSheet'
import ReviewsPanel, { useSalonReviews, firstPageKey } from './ReviewsPanel'
import BaileyMark from './BaileyMark'
import BaileyAsk from './BaileyAsk'
import NewPost from './NewPost'
import { Seg, PostCard, FavouriteGrid } from './cards'
import { PlusIcon, MailIcon, SmsIcon, TrashIcon, CopyIcon, UndoIcon, HeartIcon } from './icons'
import { sparkleFrom } from './magic'
import { usePageFacts } from '../../../../agent/bailey/skills/pageFacts'
import '../Social.css'

const Stars = ({ n }) => <span className="sx-stars-read" aria-label={`${n} out of 5 stars`}>{'★★★★★'.slice(0, n)}<span>{'★★★★★'.slice(n)}</span></span>

// Every job as a template, each in the design and colours it's shown in,
// with a heart to keep it as a favourite (a job with no design in the style
// picked isn't shown)
const TemplateGrid = ({ posts, kit, onPick, isFav, onFav, className = '' }) => (
  <div className={`sx-tpl-grid ${className}`}>
    {TYPE_ORDER.filter(type => posts[type]).map((type) => {
      const on = isFav ? isFav(posts[type]) : false
      return (
        <div key={type} className="sx-tpl-cell">
          <button type="button" className="sx-tpl" onClick={() => onPick(type)}>
            <span className="sx-tpl-art"><LazyPreview post={posts[type]} theme={postTheme(posts[type], { kit })} /></span>
            <span className="sx-tpl-name">{POST_TYPES[type].label}</span>
            <span className="sx-tpl-design">{designName(type, posts[type].family, posts[type].layout)}</span>
          </button>
          {onFav && (
            <button type="button" className={`sx-tpl-fav${on ? ' is-on' : ''}`} aria-pressed={on} aria-label={on ? `${POST_TYPES[type].label} is a favourite. Tap to take it out` : `Save ${POST_TYPES[type].label} as a favourite`} onClick={(e) => onFav(posts[type], e.currentTarget)}>
              <HeartIcon size={15} filled={on} />
            </button>
          )}
        </div>
      )
    })}
  </div>
)

// "As designed" or "In your brand", remembered on this device (as the email
// template library)
const VIEW_KEY = 'beu-social-template-view'
const readView = () => { try { return localStorage.getItem(VIEW_KEY) === 'brand' ? 'brand' : 'designed' } catch (e) { return 'designed' } }

const SocialPage = ({ dispatch, user, history, createSignal, createPlan, openPostId = null }) => {
  const brand = useSalonBrand()
  const salon = brand.salon
  const userSalon = user && user.userInfo && user.userInfo.hairdresser && user.userInfo.hairdresser.salon
  const trade = marketFor(salon && salon.businessType) || marketFor(userSalon && userSalon.businessType) || 'hair'
  const salonName = brand.spec.name || (salon && salon.salonName) || (userSalon && userSalon.salonName) || ''
  const brandColour = normHex(brand.spec.accent) || null
  const logo = brand.spec.logo || null
  const kit = useMemo(() => ({
    name: salonName || 'Your salon',
    accent: brandColour,
    logo,
    display: brand.spec.display || null,
    text: brand.spec.text || null,
  }), [salonName, brandColour, logo, brand.spec.display, brand.spec.text])
  const homeFamily = familyForLook(brand.spec.look)
  const town = townOf(salon && salon.formattedAddress)

  const [posts, setPosts] = useState(() => loadPosts())
  const [campaigns, setCampaigns] = useState([])
  const [inputs, setInputs] = useState(null) // { slots, reviews, team }
  // The newest reviews (the full list loads in its own panel)
  const salonRevs = useSalonReviews(firstPageKey)
  const reviews = salonRevs.at || salonRevs.failed ? salonRevs : null // { list, total, average }
  const [studio, setStudio] = useState(null) // { post, tool, ask }
  const [plan, setPlan] = useState(null) // { day, at } while choosing what to post
  const [asking, setAsking] = useState(false)
  const [askNote, setAskNote] = useState(null)
  const [allReviews, setAllReviews] = useState(false)
  const [listTab, setListTab] = useState('draft')
  const [view, setViewState] = useState(readView)
  const [allTemplates, setAllTemplates] = useState(false)
  const [toast, setToast] = useState(null)
  const toastTimer = useRef(null)
  const [sheet, setSheet] = useState(() => typeof window !== 'undefined' && /[?&]sheet=1/.test(window.location.search))
  const setView = (v) => { setViewState(v); try { localStorage.setItem(VIEW_KEY, v) } catch (e) { /* private mode */ } }
  const inBrand = view === 'brand' && !!brandColour

  useEffect(() => subscribePosts(() => setPosts(loadPosts())), [])
  useEffect(() => {
    if (!createSignal) return
    const a = createPlan || {}
    setPlan({
      day: a.day ? new Date(a.day) : null,
      ...(a.type ? { type: a.type } : {}),
      ...(a.staffId ? { staffId: String(a.staffId), staffName: a.staffName || '' } : {}),
      ...(a.codeId ? { codeId: String(a.codeId) } : {}),
    })
  }, [createSignal]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearTimeout(toastTimer.current), [])
  // Arriving with a post Bailey saved: open it in the studio
  useEffect(() => {
    if (!openPostId) return
    const saved = loadPosts().find(p => p.id === openPostId)
    if (saved) open(saved.family ? saved : upgradePost(saved, { family: homeFamily, brandColour }))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true
    const safe = (p, fallback) => p.catch(() => fallback)
    Promise.all([
      safe(findFreeSlots(), null),
      safe(loadItems('review', dispatch, user), []),
      safe(loadItems('team', dispatch, user), []),
    ]).then(([slots, revs, team]) => { if (alive) setInputs({ slots, reviews: revs, team }) })
    loadScheduledCampaigns(dispatch).then((list) => { if (alive) setCampaigns(list) }).catch(() => {})
    return () => { alive = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const say = (text, undo) => {
    clearTimeout(toastTimer.current)
    setToast({ text, undo, at: Date.now() })
    toastTimer.current = setTimeout(() => setToast(null), 5000)
  }

  // Posts made before the design families wear the salon's family
  // (their saved thumbnails show the old layouts, so they're drawn afresh)
  const allPosts = useMemo(() => posts.map(p => (p.family ? p : { ...upgradePost(p, { family: homeFamily, brandColour }), thumb: null })), [posts, homeFamily, brandColour])

  const ideas = useMemo(() => (inputs ? buildIdeas({
    trade,
    posts: allPosts,
    slots: inputs.slots,
    reviews: inputs.reviews,
    team: (inputs.team || []).map(m => ({ id: m.id, name: m.name, role: m.title && m.title !== 'Stylist' ? m.title : '', image: m.image })),
    salonName,
  }) : null), [inputs, allPosts, trade, salonName])
  // Each idea as the post it would make, each in its own design so the week
  // doesn't look samey (the salon's brand is one tap away in Colours)
  const ideaPosts = useMemo(() => (ideas || []).map(idea => ({
    idea,
    // Ideas are the salon's real things: only their own facts and photos go on (realPost)
    post: realPost(idea.type, { trade, family: homeFamily, brandColour, ...showcaseFor(idea.type), ...(idea.design || {}), content: idea.content, photos: idea.photos, source: idea.source, format: idea.format }),
  })), [ideas, brandColour]) // eslint-disable-line react-hooks/exhaustive-deps

  // The newest kind reviews, each already a post
  const reviewCards = useMemo(() => ((reviews && reviews.list) || []).filter(shareable).slice(0, 4)
    .map((r, i) => ({ review: r, post: reviewPost(r, { trade, index: i, brandColour }) })), [reviews, trade, brandColour])

  // The templates: every kind of post in its own design and colours, or all
  // in the salon's brand colours. Each opens New post at that kind of post.
  const templatePosts = useMemo(() => TYPE_ORDER.reduce((a, type) => {
    const show = showcaseFor(type)
    return { ...a, [type]: makePost(type, { trade, ...show, colourway: inBrand ? 'brand' : show.colourway, brandColour }) }
  }, {}), [trade, inBrand, brandColour])

  const { due, sending } = todayItems(allPosts, campaigns)
  // What New post fills its designs with: the newest review worth sharing,
  // and someone on the team (with a photo when there is one)
  const galleryReview = useMemo(() => ((reviews && reviews.list) || []).find(shareable) || null, [reviews])
  const galleryMember = useMemo(() => {
    const team = (inputs && inputs.team) || []
    const m = team.find(x => x && x.name && x.image) || team.find(x => x && x.name)
    return m ? { id: m.id, name: m.name, role: m.title && m.title !== 'Stylist' ? m.title : '', image: m.image || null } : null
  }, [inputs])

  const open = (post, tool, ask) => { hapticTick(); setPlan(null); setAllReviews(false); setAskNote(null); setStudio({ post, tool, ask }) }
  // A post chosen in New post: planned for the day it was opened for (from
  // the calendar), and opened on Text, as its design is already chosen
  const planned = (post) => {
    const day = plan && plan.day
    if (!day) return post
    return { ...post, plannedAt: (plan.at ? moment(plan.at) : moment(day).hours(12).minutes(30)).seconds(0).toISOString(), status: 'planned' }
  }
  const pickNew = (post) => open(planned(post), 'text')

  // A sentence to Bailey becomes a finished post, made with the salon's own
  // prices, diary, team and reviews, and opened with what's still needed
  const have = useMemo(() => (inputs && inputs.team ? { team: inputs.team.map(m => ({ id: m.id, name: m.name, role: m.title && m.title !== 'Stylist' ? m.title : '', image: m.image })) } : null), [inputs])
  const gather = useGatherAsk(have)
  const captionFor = (p, voice) => buildCaption(p, { voice, salon: salonName, town })
  const askBailey = async (text) => {
    setAskNote(null)
    const req = parseRequest(text, { now: new Date() })
    if (req.undo) { setAskNote(NOT_UNDERSTOOD_HOME); return false }
    const about = req.job || req.names.length || req.reviewPick || Object.keys(req.fields).some(k => !['dates', 'date', 'times', 'more'].includes(k))
    // Just a when ("post something Friday at 6pm"): pick what to post, planned for then
    const when = (req.schedule && req.schedule.at) || req.when
    const bare = !String(req.body || '').replace(/\b(?:post|share|something|anything|a|an|the|one|please|for|me|it|this|on|at)\b/gi, '').replace(/[^a-z]/gi, '')
    if (!about && req.schedule && bare && !(req.look && req.look.known)) { hapticTick(); setPlan({ day: when, at: when }); return true }
    setAsking(true)
    const [data, online] = await Promise.all([gather(req, null).catch(() => ({})), baileyReady()])
    const now = new Date()
    const ctx = { ...data, now, kit, trade, captionFor, text }
    if (online) {
      // The right kind of post from what was said, then Bailey fills it in
      // (a day in the words is Bailey's to place: "closed Monday" isn't when to post)
      const start = createFromRequest({ ...req, when: null }, ctx)
      const r = await askBaileyAI({ brief: briefFor(start.post, { text, kit, trade, facts: data, now, fresh: true }), text, reviews: reviewsFor(data) })
      if (r.ok) {
        const edited = applyEdit(start.post, r.edit, { ...ctx, fresh: true })
        // One stylist's free times (asked for by name) go on as theirs
        const res = { ...edited, post: withStylist(edited.post, ctx) }
        setAsking(false)
        if (!about && !res.changes.length) { setAskNote((res.needs[0] && res.needs[0].text) || NOT_UNDERSTOOD_HOME); return false }
        const missing = missingFacts(res.post)
        hapticSuccess()
        open({ ...res.post, name: res.post.name || (isNews(res.post) ? 'Salon news' : ''), fromBailey: true }, 'bailey', {
          asked: text,
          say: res.say,
          changes: summaryOf(res.post, [...start.changes, ...res.changes], now),
          needs: [...missing, ...res.needs.filter(n => !missing.some(m => m.text === n.text || new RegExp(`\\b${m.text.split(/[\\s.]+/).filter(Boolean).slice(-1)[0] || '#'}\\b`, 'i').test(n.text)))],
          notes: res.notes,
          history: r.history,
        })
        return true
      }
    }
    setAsking(false)
    // The rules, when Bailey can't be reached
    if (!req.known) { setAskNote(NOT_UNDERSTOOD_HOME); return false }
    if (!about) {
      if (when && !(req.look && req.look.known)) { hapticTick(); setPlan({ day: when, at: when }); return true }
      setAskNote(req.look && req.look.known ? 'I can do that look. What’s the post about? Add it, like “20% off colour this week, make it pink”.' : 'What’s the post about? Try “20% off colour this week” or “post my free slots”.')
      return false
    }
    const res = createFromRequest(req, ctx)
    hapticSuccess()
    open(res.post, 'bailey', { asked: text, changes: res.changes, needs: res.needs, notes: online ? [...res.notes, 'Bailey was busy, so I did the simple parts. Ask again in the studio for the rest.'] : res.notes, req, given: res.given, post: res.post })
    return true
  }
  // Favourites: designs kept for next time, and the hearts that keep them
  const [favourites, setFavourites] = useState(loadFavourites)
  useEffect(() => subscribeFavourites(() => setFavourites(loadFavourites())), [])
  const isFav = (post) => favourites.length > 0 && !!findFavourite(favouriteFrom(post))
  const removeFav = (fav) => {
    const index = favourites.findIndex(f => f.id === fav.id)
    removeFavourite(fav.id)
    hapticTick()
    say('Taken out of your favourites.', () => { restoreFavourite(fav, index); hapticTick() })
  }
  const toggleTemplateFav = (post, el) => {
    const found = findFavourite(favouriteFrom(post))
    if (found) { removeFav(found); return }
    addFavourite(favouriteFrom(post))
    hapticSuccess()
    sparkleFrom(el, 8)
    say(`${POST_TYPES[post.type].label} saved to your favourites.`)
  }
  const openFavourite = (fav) => open(planned(postFromFavourite(fav, { trade, brandColour })), plan ? 'text' : undefined)
  const favouriteGrid = favourites.length > 0 && (
    <FavouriteGrid favourites={favourites} kit={kit} trade={trade} brandColour={brandColour} onPick={openFavourite} onRemove={removeFav} />
  )

  const makeFromReview = (r, i = 0) => open(reviewPost(r, { trade, index: i, brandColour }))
  const addReview = () => open(makePost('review', { trade, family: 'poster', colourway: 'red', brandColour, content: { quote: '', name: '', service: '', rating: 5 } }), 'text')

  const lists = {
    draft: allPosts.filter(p => p.status === 'draft'),
    planned: allPosts.filter(p => p.status === 'planned').sort((a, b) => new Date(a.plannedAt) - new Date(b.plannedAt)),
    posted: allPosts.filter(p => p.status === 'posted').sort((a, b) => new Date(b.postedAt) - new Date(a.postedAt)),
  }
  // What Bailey can see of this page, for the desk pet
  const nextPlanned = lists.planned[0]
  const slotsSaid = inputs && inputs.slots && inputs.slots.count
    ? `${inputs.slots.count} free ${inputs.slots.count === 1 ? 'time' : 'times'} ${/^(today|tomorrow)$/i.test(inputs.slots.dayLabel || '') ? inputs.slots.dayLabel.toLowerCase() : `on ${inputs.slots.dayLabel}`} in the diary`
    : null
  usePageFacts('social', '/marketing-social', [
    `${lists.draft.length} drafts, ${lists.planned.length} planned and ${lists.posted.length} shared posts`,
    nextPlanned && nextPlanned.plannedAt ? `Next planned: ${(POST_TYPES[nextPlanned.type] || {}).label || 'a post'} on ${moment(nextPlanned.plannedAt).format('ddd D MMM, h:mma')}` : null,
    slotsSaid,
    reviews && Number(reviews.total) > 0 ? `${reviews.total} reviews${reviews.average ? `, ${reviews.average} stars on average` : ''}` : null,
    studio && studio.post ? `Editing a ${(POST_TYPES[studio.post.type] || {}).label || 'post'} in the studio` : null,
  ])
  const duplicate = (p) => {
    const { id, createdAt, updatedAt, postedAt, plannedAt, ...rest } = p
    savePost({ ...rest, status: 'draft', name: p.name ? `${p.name} (copy)` : '' })
    hapticSuccess()
    setListTab('draft')
    say('Copied. The copy is in Drafts.')
  }
  // Deleting is immediate, with a moment to take it back
  const remove = (p) => {
    const kept = posts.find(x => x.id === p.id) || p
    deletePost(p.id)
    hapticTick()
    say(`Deleted ${p.name || POST_TYPES[p.type].label.toLowerCase()}.`, () => { restorePost(kept); hapticTick() })
  }
  const templateControls = brandColour ? (
    <div className="sx-tpl-controls">
      <Seg label="Colours" value={inBrand ? 'brand' : 'designed'} onChange={setView} options={[{ id: 'designed', label: 'As designed' }, { id: 'brand', label: 'In your brand' }]} />
    </div>
  ) : null
  const found = (ideas || []).length + due.length + sending.length

  const hereOnly = keptHereOnly()
  return (
    <div className="sx-page">
      {hereOnly && (
        <p role="status" style={{ margin: '0 0 14px', fontSize: 12.5, lineHeight: 1.45, color: '#B42318' }}>
          {hereOnly === 'managed'
            ? 'Your salon runs your marketing, so posts you make here are saved on this device only. Nobody else sees them, and they’re lost if this browser is cleared.'
            : 'Your account can’t save posts to the salon, so these are kept on this device only. Ask the owner for access to Marketing to keep them safe.'}
        </p>
      )}
      <section className="sx-week" aria-label="This week">
        <div className="sx-week-head">
          <BaileyMark size={48} thinking={!ideas || asking} />
          <div className="sx-week-text">
            <h2 className="sx-h2">This week</h2>
            <p className="sx-week-line">
              {!ideas ? 'Bailey is looking at your diary and reviews…'
                : found ? `Bailey found ${found === 1 ? 'one thing' : `${found} things`} worth posting, from your diary and reviews.`
                  : 'Nothing needs a post right now. Your diary’s full and you’ve posted this week.'}
            </p>
          </div>
        </div>
        <BaileyAsk onAsk={askBailey} busy={asking} note={askNote} />
        <div className="sx-cards">
          {due.map(p => (
            <PostCard
              key={p.id}
              post={p}
              theme={postTheme(p, { kit })}
              thumb={p.thumb}
              tone="due"
              kind={isDue(p) ? 'Ready to post now' : `Post at ${moment(p.plannedAt).format('h:mma')}`}
              title={p.name || POST_TYPES[p.type].label}
              why={moment(p.plannedAt).isSame(moment(), 'day') ? 'You planned this for today.' : `Planned for ${moment(p.plannedAt).calendar(null, { lastDay: '[yesterday]', lastWeek: 'dddd', sameElse: 'D MMMM' })} and not shared yet.`}
              onClick={() => open(p)}
            />
          ))}
          {sending.map(c => (
            <button key={c.id} type="button" className="sx-card sx-card--campaign" onClick={() => history.push('/marketing-campaigns', { openCampaignId: c.id })}>
              <span className="sx-card-art sx-card-art--campaign">{c.channel === 'sms' ? <SmsIcon size={26} /> : <MailIcon size={26} />}<span>{moment(c.at).format('h:mma')}</span></span>
              <span className="sx-card-text">
                <span className="sx-card-kind">{c.channel === 'sms' ? 'Text' : 'Email'} going out today</span>
                <strong className="sx-card-title">{c.name}</strong>
                <span className="sx-card-why">A post the same day helps it land.</span>
              </span>
            </button>
          ))}
          {!ideas && [0, 1, 2].map(i => <div key={i} className="sx-card is-loading" aria-hidden><span className="sx-card-art" /><span className="sx-card-text"><span className="sx-line" /><span className="sx-line is-short" /></span></div>)}
          {ideaPosts.map(({ idea, post }) => (
            <PostCard
              key={idea.id}
              post={post}
              theme={postTheme(post, { kit })}
              kind={post.format === 'story' ? 'Story' : 'Post'}
              title={idea.title}
              why={idea.why}
              onClick={() => open(post)}
            />
          ))}
        </div>
      </section>

      <section className="sx-section" aria-label="Your reviews">
        <div className="sx-section-head">
          <div className="sx-section-title">
            <h2 className="sx-h2">Your reviews</h2>
            <p className="sx-section-line">
              {reviews && reviews.average ? `${reviews.average.toFixed(1)} stars${reviews.total ? ` from ${reviews.total} reviews` : ''}. ` : ''}
              Share one word for word, in a tap.
            </p>
          </div>
          <div className="sx-section-actions">
            <button type="button" className="sx-btn" onClick={addReview}><PlusIcon size={15} />Add a review</button>
            {reviews && reviews.list.length > 0 && <button type="button" className="sx-btn" onClick={() => { hapticTick(); setAllReviews(true) }}>See all</button>}
          </div>
        </div>
        {!reviews && <div className="sx-review-grid">{[0, 1, 2, 3].map(i => <div key={i} className="sx-card is-loading" aria-hidden><span className="sx-card-art" /></div>)}</div>}
        {reviews && reviewCards.length > 0 && (
          <div className="sx-review-grid">
            {reviewCards.map(({ review, post }, i) => (
              <button key={review.id} type="button" className="sx-review-card" onClick={() => makeFromReview(review, i)}>
                <span className="sx-card-art"><Preview post={post} theme={postTheme(post, { kit })} /></span>
                <span className="sx-review-meta">
                  <Stars n={review.rating} />
                  <span className="sx-review-who">{review.firstName}<span className="sx-review-when">{reviewWhen(review.at)}</span></span>
                </span>
                {isShared(review) && <span className="sx-review-shared">Shared</span>}
              </button>
            ))}
          </div>
        )}
        {reviews && !reviewCards.length && (
          <div className="sx-empty-card">
            <p>No four or five star reviews yet. When clients leave one, it shows here ready to share. You can also add one from Google or a message.</p>
          </div>
        )}
      </section>

      {favouriteGrid && (
        <section className="sx-section" aria-label="Your favourites">
          <div className="sx-section-head">
            <div className="sx-section-title">
              <h2 className="sx-h2">Your favourites</h2>
              <p className="sx-section-line">The designs you use most. Tap one to start a new post in it.</p>
            </div>
          </div>
          {favouriteGrid}
        </section>
      )}

      <section className="sx-section" aria-label="Templates">
        <div className="sx-section-head">
          <div className="sx-section-title">
            <h2 className="sx-h2">Start from a template</h2>
            <p className="sx-section-line">Twelve kinds of post, in three styles, thirty layouts and plenty of colours.</p>
          </div>
          {templateControls}
        </div>
        <TemplateGrid posts={templatePosts} kit={kit} onPick={(type) => { hapticTick(); setPlan({ type }) }} isFav={isFav} onFav={toggleTemplateFav} className={allTemplates ? 'is-all' : ''} />
        {!allTemplates && <button type="button" className="sx-btn sx-show-all" onClick={() => { hapticTick(); setAllTemplates(true) }}>Show all {TYPE_ORDER.length} templates</button>}
      </section>

      <MarketingCalendar
        dispatch={dispatch}
        trade={trade}
        kit={kit}
        posts={allPosts}
        createKinds={['post', 'email', 'sms']}
        onOpenPost={(p) => open(p)}
        onOpenCampaign={(c) => history.push('/marketing-campaigns', { openCampaignId: c.id })}
        onCreate={(day, kind) => {
          if (kind === 'post') setPlan({ day })
          else history.push('/marketing-campaigns', { compose: kind })
        }}
      />

      <section className="sx-section" aria-label="Your posts">
        <div className="sx-section-head">
          <div className="sx-section-title">
            <h2 className="sx-h2">Your posts</h2>
            <p className="sx-section-line">Everything you make saves itself here: drafts to finish, posts waiting for their day, and what you’ve shared.</p>
          </div>
          <Seg label="Your posts" value={listTab} onChange={setListTab} options={[['draft', 'Drafts'], ['planned', 'Planned'], ['posted', 'Shared']].map(([id, label]) => ({ id, label: `${label}${lists[id].length ? ` ${lists[id].length}` : ''}` }))} />
        </div>
        {lists[listTab].length ? (
          <div className="sx-posts">
            {lists[listTab].map(p => (
              <div key={p.id} className="sx-post" data-post-id={p.id}>
                <button type="button" className="sx-post-art" onClick={() => open(p)} aria-label={`Open ${p.name || POST_TYPES[p.type].label}`}>
                  {p.thumb ? <img src={p.thumb} alt="" loading="lazy" className={p.format === 'story' ? 'is-story' : ''} /> : <LazyPreview post={p} theme={postTheme(p, { kit })} />}
                </button>
                <div className="sx-post-meta">
                  <span className="sx-post-name">{p.name || POST_TYPES[p.type].label}</span>
                  <span className="sx-post-when">
                    {p.status === 'posted' ? `Shared ${moment(p.postedAt).format('D MMM')}` : p.status === 'planned' ? moment(p.plannedAt).format('ddd D MMM, h:mma') : `Edited ${moment(p.updatedAt).fromNow()}`}
                  </span>
                </div>
                <div className="sx-post-actions">
                  <button type="button" className="sx-icon-btn" onClick={() => duplicate(p)} aria-label="Make a copy" title="Make a copy"><CopyIcon size={16} /></button>
                  <button type="button" className="sx-icon-btn" onClick={() => remove(p)} aria-label="Delete post" title="Delete"><TrashIcon /></button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="sx-empty">{listTab === 'draft' ? 'Nothing in progress. Anything you start is kept here until you share it.' : listTab === 'planned' ? 'Pick a day in a post’s Caption tool and it waits here, and on your calendar.' : 'Posts you’ve shared show here, so you can see what’s gone out.'}</p>
        )}
      </section>

      {plan && (
        <NewPost
          plan={plan}
          kit={kit}
          trade={trade}
          brandColour={brandColour}
          ideas={ideas ? ideaPosts : null}
          favourites={favourites}
          review={galleryReview}
          member={galleryMember}
          view={view}
          onView={setView}
          isFav={isFav}
          onFav={toggleTemplateFav}
          onPick={pickNew}
          onPickFavourite={openFavourite}
          onRemoveFavourite={removeFav}
          onAsk={askBailey}
          asking={asking}
          askNote={askNote}
          onClose={() => setPlan(null)}
        />
      )}
      {allReviews && <ReviewsPanel mode="make" onUse={(r) => makeFromReview(r)} onClose={() => setAllReviews(false)} />}
      {studio && (
        <PostStudio
          key={studio.post.id || 'new'}
          post={studio.post}
          startTool={studio.tool}
          askStart={studio.ask}
          have={have}
          kit={kit}
          trade={trade}
          salonName={salonName}
          town={town}
          salon={salon}
          onClose={() => setStudio(null)}
        />
      )}
      {toast && (
        <div className="sx-toast" role="status" key={toast.at}>
          <span>{toast.text}</span>
          {toast.undo && <button type="button" onClick={() => { toast.undo(); setToast(null) }}><UndoIcon size={14} />Undo</button>}
        </div>
      )}
      {sheet && <DesignSheet trade={trade} kit={kit} onClose={() => setSheet(false)} />}
    </div>
  )
}

export default SocialPage
